import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { getDatabases, getDeliveryRecipients, createDeliveryGrant } from "../../api/adminApi";

/**
 * "Deliver…" — hands the documents in front of you to an external recipient, without leaving search.
 *
 * The natural moment to deliver a customer their statement is while looking at it, not after copying
 * ids into an admin form. This mints a DocumentSet grant: exactly these documents, frozen, and nothing
 * else — the recipient's other documents are NOT included, which is what makes it safe to fire from a
 * results list.
 *
 * The link it produces exists only in this dialog. The server stored a hash, so closing without copying
 * means minting a new one; the dialog says so rather than letting the operator find out later.
 *
 * Props: open, dbName, documentIds[], onClose
 */
export default function DeliverModal({ open, dbName, documentIds = [], onClose }) {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [recipients, setRecipients] = useState(null);
  const [dbInfoId, setDbInfoId] = useState(null);
  const [recipientId, setRecipientId] = useState("");
  const [days, setDays] = useState("");
  const [requireCode, setRequireCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [minted, setMinted] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = useCallback(() => {
    setRecipientId(""); setDays(""); setRequireCode(false);
    setError(""); setMinted(null); setCopied(false);
  }, []);

  useEffect(() => {
    if (!open || !token) return undefined;
    let cancelled = false;
    reset();
    (async () => {
      try {
        const [people, dbs] = await Promise.all([
          getDeliveryRecipients(token, {}),
          getDatabases(token).catch(() => []),
        ]);
        if (cancelled) return;
        setRecipients(people?.recipients || []);
        const list = Array.isArray(dbs) ? dbs : dbs?.databases || [];
        const match = list.find((d) => (d.dbName || d.DbName || d.name) === dbName);
        setDbInfoId(match ? (match.id ?? match.Id) : null);
      } catch (e) {
        if (!cancelled) { setError(e?.message || String(e)); setRecipients([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, token, dbName, reset]);

  const eligible = useMemo(
    () => (recipients || []).filter((r) => r.status === "Active"),
    [recipients]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await createDeliveryGrant(token, {
        recipientId: Number(recipientId),
        scopeKind: "DocumentSet",
        dbInfoId,
        documentIds: documentIds.map(Number).filter((n) => Number.isFinite(n) && n > 0),
        label: t("deliverModal.label", {
          defaultValue: "{{count}} document(s) from {{db}}",
          count: documentIds.length, db: dbName,
        }),
        validitySeconds: days ? Number(days) * 86400 : null,
        requireAccessCode: requireCode,
      });
      setMinted(result);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(minted.link || minted.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the value is on screen to select by hand */ }
  };

  return (
    <div className="dbModalOverlay" onClick={onClose}>
      <div className="dbModalCard dbModalConfirmCard" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="dbModalConfirmBadge dbModalConfirmBadge--info">📤</div>
        <div className="dbModalTitle">{t("deliverModal.title", { defaultValue: "Deliver documents" })}</div>

        {!minted ? (
          <form onSubmit={submit}>
            <p style={{ fontSize: 13, color: "#475569", margin: "0 0 16px" }}>
              {t("deliverModal.lead", {
                defaultValue: "{{count}} document(s) from {{db}}. The recipient sees only these — not the rest of their documents.",
                count: documentIds.length, db: dbName,
              })}
            </p>

            {error && <div className="asf-alert">⚠ {error}</div>}

            <div className="asf-field" style={{ marginBottom: 14 }}>
              <label className="asf-lbl">{t("deliverModal.recipient", { defaultValue: "Recipient" })}</label>
              {recipients === null ? (
                <div style={{ fontSize: 13, color: "#94a3b8" }}>{t("common.loading", { defaultValue: "Loading…" })}</div>
              ) : eligible.length === 0 ? (
                <div style={{ fontSize: 13, color: "#b45309" }}>
                  {t("deliverModal.noRecipients", {
                    defaultValue: "No active recipients yet. Create one in Admin → Delivery first.",
                  })}
                </div>
              ) : (
                <select className="asf-input" value={recipientId} required
                  onChange={(ev) => setRecipientId(ev.target.value)}>
                  <option value="">{t("common.select", { defaultValue: "Select…" })}</option>
                  {eligible.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.displayName}{r.externalRef ? ` — ${r.externalRef}` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16 }}>
              <div className="asf-field" style={{ flex: "0 1 140px" }}>
                <label className="asf-lbl">{t("deliverModal.validDays", { defaultValue: "Valid (days)" })}</label>
                <input className="asf-input" type="number" min="1" value={days}
                  placeholder={t("deliverModal.policyDefault", { defaultValue: "policy default" })}
                  onChange={(ev) => setDays(ev.target.value)} />
              </div>
              <label className="asf-switch" style={{ paddingBottom: 10 }}>
                <input type="checkbox" checked={requireCode} onChange={(ev) => setRequireCode(ev.target.checked)} />
                <span className="asf-switch__track" />
                {t("deliverModal.requireCode", { defaultValue: "Require access code" })}
              </label>
            </div>

            {dbInfoId == null && recipients !== null && (
              <div className="asf-alert">
                ⚠ {t("deliverModal.noBase", {
                  defaultValue: "This document base is not available for delivery. Map its recipient-key column in Admin → Delivery.",
                })}
              </div>
            )}

            <div className="dbModalConfirmActions">
              <button type="button" className="dbModalCancel" onClick={onClose}>
                {t("common.cancel", { defaultValue: "Cancel" })}
              </button>
              <button type="submit" className="dbModalConfirmBtn dbModalConfirmBtn--info"
                disabled={busy || !recipientId || dbInfoId == null || documentIds.length === 0}>
                {busy
                  ? t("common.loading", { defaultValue: "Loading…" })
                  : t("deliverModal.create", { defaultValue: "Create link" })}
              </button>
            </div>
          </form>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "#475569", margin: "0 0 14px" }}>
              {t("delivery.linkHint", { defaultValue: "Send this to the recipient yourself — nothing is sent from here." })}
            </p>
            <div style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, wordBreak: "break-all",
              background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", color: "#0f172a",
            }}>
              {minted.link || minted.token}
            </div>
            {!minted.link && (
              <p style={{ fontSize: 12, color: "#64748b" }}>
                {t("delivery.noBaseUrl", {
                  defaultValue: "No portal address is configured, so this is the raw token. Set ApplicationSettings:Portal_BaseUrl to get a ready-made link.",
                })}
              </p>
            )}
            <div className="dbModalConfirmBanner dbModalConfirmBanner--warn" style={{ marginTop: 14 }}>
              <p>
                {t("delivery.onceOnly", {
                  defaultValue: "This is shown once. Only a hash was stored, so it cannot be retrieved again — if it is lost, issue a new one.",
                })}
              </p>
            </div>
            <div className="dbModalConfirmActions">
              <button type="button" className="dbModalCancel" onClick={copy}>
                {copied ? t("common.copied", { defaultValue: "Copied" }) : t("common.copy", { defaultValue: "Copy" })}
              </button>
              <button type="button" className="dbModalConfirmBtn dbModalConfirmBtn--info" onClick={onClose}>
                {t("delivery.savedIt", { defaultValue: "I've saved it" })}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
